import React, {useEffect, useState} from 'react';
import {Box, Button, TextField} from '@mui/material';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import PropTypes from 'prop-types';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

export const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

const Payment = ({
                     index,
                     id,
                     description,
                     paymentLink = '',
                     onChange,
                     isEditMode,
                     isFormArchived,
                     placeholderValues,
                     readOnly,
                     accessLevel,
                     audience,
                     recipientType,
                     submitMode
                 }) => {
    const [currentDescription, setCurrentDescription] = useState(description ?? '');
    const [errorText, setErrorText] = useState('');

    useEffect(() => {
        setCurrentDescription(description ?? '');
    }, [description]);

    const handleDescriptionChange = (nextValue) => {
        setCurrentDescription(nextValue);

        if (!onChange) return;

        onChange({
            index,
            description: stripHtmlTags(nextValue),
            formattedDescription: nextValue,
        });
    };

    const validateUrl = (url) => {
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            setErrorText('');
            return false;
        }

        if (!trimmedUrl.startsWith('https://')) {
            setErrorText('URL must start with https://');
            return false;
        }

        setErrorText('');
        return true;
    };

    const handleUrlChange = (e) => {
        const newUrl = e.target.value;

        validateUrl(newUrl);

        if (onChange) {
            onChange({
                index,
                paymentLink: newUrl,
            });
        }
    };

    const openPopout = () => {
        if (!errorText && paymentLink) {
            window.open(paymentLink, '_blank');
        }
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <RichEditor
                        value={description}
                        onChange={onChange}
                        id={id}
                        index={index}
                        isEditMode={isEditMode}
                        isFormLive={isFormArchived}
                        accessLevel={accessLevel}
                        currentDescription={currentDescription}
                        setCurrentDescription={handleDescriptionChange}
                        sx={{mb: 2}}
                        audience={audience}
                        recipientType={recipientType}
                    />
                )
                : (
                    <RichEditorResultText
                        id={id}
                        description={description}
                        placeholderValues={placeholderValues}
                        readonly={readOnly}
                        enableValidation={false}
                        sx={{mb: 2}}
                        audience={audience}
                        recipientType={recipientType}
                        submitMode={submitMode}
                    />
                )}

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1
            }}>
                {isEditMode && (
                    <TextField
                        placeholder="Enter payment link here"
                        value={paymentLink}
                        onChange={handleUrlChange}
                        error={!!errorText}
                        helperText={errorText}
                        sx={{
                            flexGrow: 1,
                            minWidth: '200px',
                            pr: 1,
                            py: 1
                        }}
                    />
                )}
                <Button
                    onClick={openPopout}
                    startIcon={<PaymentOutlinedIcon/>}
                    variant="outlined"
                    disabled={!!errorText || !paymentLink || readOnly}
                >
                    Make Payment
                </Button>
            </Box>
        </Box>
    );
};

Payment.propTypes = {
    id: PropTypes.string,
    description: PropTypes.string,
    paymentLink: PropTypes.string,
    index: PropTypes.number,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    submitMode: PropTypes.bool
};

export default Payment;
